/**
 * Supabase Tenant Queries Implementation
 *
 * Implements the ITenantQueries interface using direct Supabase table queries.
 * This approach is more reliable than RPC functions and avoids race conditions.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import type { ITenantQueries } from '../interfaces/ITenantQueries';
import type { UserWithTenant } from '../interfaces/types';

export class SupabaseTenantQueries implements ITenantQueries {
  constructor(private supabase: SupabaseClient) {}

  async getUserByEmailAndTenantSlug(
    email: string,
    tenantSlug: string
  ): Promise<UserWithTenant | null> {
    // Query profiles with tenant and roles in a single query
    const { data: profileData, error: profileError } = await this.supabase
      .from('profiles')
      .select(`
        id,
        email,
        name,
        avatar_url,
        phone,
        is_active,
        tenant_id,
        tenants:tenant_id (
          id,
          name,
          slug,
          status,
          settings
        ),
        user_roles (
          roles (
            name
          )
        )
      `)
      .eq('email', email)
      .eq('tenants.slug', tenantSlug)
      .eq('is_active', true)
      .single();

    if (profileError || !profileData) {
      return null;
    }

    // Extract tenant data
    const tenant = Array.isArray(profileData.tenants)
      ? profileData.tenants[0]
      : profileData.tenants;

    if (!tenant) {
      return null;
    }

    // Extract roles
    const roles = profileData.user_roles
      ?.map((ur: any) => ur.roles?.name)
      .filter((name: string | undefined) => name !== undefined) || [];

    return {
      id: profileData.id,
      email: profileData.email,
      name: profileData.name,
      avatar_url: profileData.avatar_url,
      phone: profileData.phone,
      is_active: profileData.is_active,
      tenant: {
        id: tenant.id,
        name: tenant.name,
        slug: tenant.slug,
        status: tenant.status,
        settings: tenant.settings,
      },
      roles,
    };
  }

  async getUserProfileWithTenant(userId: string): Promise<UserWithTenant | null> {
    // Query profiles with tenant and roles in a single query
    const { data: profileData, error: profileError } = await this.supabase
      .from('profiles')
      .select(`
        id,
        email,
        name,
        avatar_url,
        phone,
        is_active,
        tenant_id,
        tenants:tenant_id (
          id,
          name,
          slug,
          status,
          settings
        ),
        user_roles (
          roles (
            name
          )
        )
      `)
      .eq('id', userId)
      .eq('is_active', true)
      .single();

    if (profileError || !profileData) {
      return null;
    }

    // Extract tenant data
    const tenant = Array.isArray(profileData.tenants)
      ? profileData.tenants[0]
      : profileData.tenants;

    if (!tenant) {
      return null;
    }

    // Extract roles
    const roles = profileData.user_roles
      ?.map((ur: any) => ur.roles?.name)
      .filter((name: string | undefined) => name !== undefined) || [];

    return {
      id: profileData.id,
      email: profileData.email,
      name: profileData.name,
      avatar_url: profileData.avatar_url,
      phone: profileData.phone,
      is_active: profileData.is_active,
      tenant: {
        id: tenant.id,
        name: tenant.name,
        slug: tenant.slug,
        status: tenant.status,
        settings: tenant.settings,
      },
      roles,
    };
  }

  async getUserTenantId(userId: string): Promise<string | null> {
    const { data, error } = await this.supabase
      .from('profiles')
      .select('tenant_id')
      .eq('id', userId)
      .single();

    if (error || !data) {
      return null;
    }

    return data.tenant_id;
  }

  async userBelongsToTenant(userId: string, tenantId: string): Promise<boolean> {
    const { data, error } = await this.supabase
      .from('profiles')
      .select('id')
      .eq('id', userId)
      .eq('tenant_id', tenantId)
      .eq('is_active', true)
      .single();

    return !error && !!data;
  }
}

